/**
 * Build a hierarchical tree structure from a flat list of file paths
 * @param {string[]} filePaths - Array of file paths
 * @returns {Object} Tree structure with folders and files
 */
export function buildFileTree(filePaths) {
  const tree = {};
  
  // Helper function to check if a file path has a supported extension
  const hasSupportedExtension = (path) => {
    return path.endsWith('.py') ||   // Python
           path.endsWith('.js') ||    // JavaScript
           path.endsWith('.ts') ||    // TypeScript
           path.endsWith('.tsx') ||   // TypeScript React
           path.endsWith('.jsx') ||   // JavaScript React
           path.endsWith('.vue');     // Vue
  };
  
  // First pass: Identify files (Python, JS, TS, Vue) that have function or class children
  const filesWithFunctions = new Set();
  const filesWithClasses = new Set();
  // First pass: Identify classes that have method children
  const classesWithMethods = new Set();
  filePaths.forEach(filePath => {
    // Check if this is a function path (ends with .function)
    if (filePath.endsWith('.function')) {
      // Extract parent file path
      // e.g., "backend/latex_pint.py/clear_variables.function" -> "backend/latex_pint.py"
      // e.g., "src/App.js/App.function" -> "src/App.js"
      const lastSlashIndex = filePath.lastIndexOf('/');
      if (lastSlashIndex !== -1) {
        const parentPath = filePath.substring(0, lastSlashIndex);
        // Check if parent is a supported file type (Python, JS, TS, Vue)
        if (hasSupportedExtension(parentPath)) {
          filesWithFunctions.add(parentPath);
        }
      }
    }
    // Check if this is a class path (ends with .class)
    if (filePath.endsWith('.class')) {
      // Extract parent file path
      // e.g., "codemaps/public/Real.py/Real.class" -> "codemaps/public/Real.py"
      const lastSlashIndex = filePath.lastIndexOf('/');
      if (lastSlashIndex !== -1) {
        const parentPath = filePath.substring(0, lastSlashIndex);
        // Check if parent is a supported file type (Python, JS, TS, Vue)
        if (hasSupportedExtension(parentPath)) {
          filesWithClasses.add(parentPath);
        }
      }
    }
    // Check if this is a method path (ends with .method)
    if (filePath.endsWith('.method')) {
      // Extract parent class path
      // e.g., "Real.py/Real.class/Real.__init__.method" -> "Real.py/Real.class"
      const lastSlashIndex = filePath.lastIndexOf('/');
      if (lastSlashIndex !== -1) {
        const parentPath = filePath.substring(0, lastSlashIndex);
        // Check if parent is a class (ends with .class)
        if (parentPath.endsWith('.class')) {
          classesWithMethods.add(parentPath);
        }
      }
    }
  });

  // Second pass: Build the tree
  filePaths.forEach(filePath => {
    // Check if this is a directory path (ends with '/')
    const isDirectoryPath = filePath.endsWith('/');
    // Check if this is a function path (ends with '.function')
    const isFunctionPath = filePath.endsWith('.function');
    // Check if this is a method path (ends with '.method')
    const isMethodPath = filePath.endsWith('.method');
    // Remove trailing slash for processing
    const cleanPath = isDirectoryPath ? filePath.slice(0, -1) : filePath;
    const parts = cleanPath.split('/').filter(part => part !== ''); // Filter out empty parts
    let current = tree;

    parts.forEach((part, index) => {
      const isLastPart = index === parts.length - 1;
      const path = parts.slice(0, index + 1).join('/');
      
      // Check if this part is a file (Python, JS, TS, Vue) that should have children
      // Check if the path (at this point) is a file that has functions or classes as children
      const isFileWithFunctions = filesWithFunctions.has(path);
      const isFileWithClasses = filesWithClasses.has(path);
      const isFileWithChildren = isFileWithFunctions || isFileWithClasses;
      // Also check if this is a file type (has supported extension) and might have children
      const isFileType = hasSupportedExtension(path);
      // Check if this part is a class (ends with .class) - it should have children if it has methods
      const isClassPath = path.endsWith('.class');
      const isClassWithMethods = isClassPath && classesWithMethods.has(path);
      
      if (!current[part]) {
        // Determine type: function, method, class, file, or folder
        let nodeType;
        if (isMethodPath && isLastPart) {
          nodeType = 'method';
        } else if (isFunctionPath && isLastPart) {
          nodeType = 'function';
        } else if (isClassPath) {
          // Any path ending with .class is a class type
          nodeType = 'class';
        } else if (isFileWithChildren) {
          // File with functions or classes (Python, JS, TS, Vue) - treat as file but with children support
          nodeType = 'file';
        } else if (isLastPart && !isDirectoryPath) {
          nodeType = 'file';
        } else if (!isLastPart && isFileType) {
          // This is a file type in the middle of a path (e.g., Real.py in Real.py/Real.class)
          // It might have classes as children, so check if it's in filesWithClasses
          nodeType = 'file';
        } else {
          nodeType = 'folder';
        }
        
        // Determine if this node should have children
        const shouldHaveChildren = nodeType === 'folder' || 
                                   nodeType === 'class' || 
                                   isFileWithChildren ||
                                   (nodeType === 'file' && !isLastPart && isFileType && filesWithClasses.has(path));
        
        current[part] = {
          id: path,
          name: part,
          type: nodeType,
          path: path,
          children: shouldHaveChildren ? {} : null
        };
      } else {
        // Node already exists - check if we need to convert it to support children
        const shouldHaveChildren = isFileWithChildren || 
                                   (current[part].type === 'file' && !isLastPart && isFileType && filesWithClasses.has(path));
        if (shouldHaveChildren && current[part].type === 'file' && current[part].children === null) {
          // Convert existing file node to support children
          current[part].children = {};
        }
        if (isClassPath && current[part].type !== 'class') {
          // Convert existing node to class type if it's a class path
          current[part].type = 'class';
          if (current[part].children === null) {
            current[part].children = {};
          }
        }
      }

      // Navigate into children if this is a folder, class, or a file with children (functions or classes)
      if (current[part].type === 'folder' || current[part].type === 'class' || (current[part].type === 'file' && current[part].children !== null)) {
        current = current[part].children;
      }
    });
  });

  // Convert object to array and sort
  function objectToArray(obj) {
    return Object.values(obj)
      .map(item => {
        // Process children for folders, classes, and files with children
        if (item.children && (item.type === 'folder' || item.type === 'class' || (item.type === 'file' && item.children !== null))) {
          item.children = objectToArray(item.children);
        }
        return item;
      })
      .sort((a, b) => {
        // Sort order: folders first, then classes, then files with children, then regular files, then functions/methods
        // Within each category, sort alphabetically
        
        // Functions and methods always come last
        if ((a.type === 'function' || a.type === 'method') && (b.type !== 'function' && b.type !== 'method')) return 1;
        if ((b.type === 'function' || b.type === 'method') && (a.type !== 'function' && a.type !== 'method')) return -1;
        
        // Folders come first
        if (a.type === 'folder' && b.type !== 'folder') return -1;
        if (b.type === 'folder' && a.type !== 'folder') return 1;
        
        // Classes come after folders but before files
        if (a.type === 'class' && b.type !== 'class' && b.type !== 'folder') return -1;
        if (b.type === 'class' && a.type !== 'class' && a.type !== 'folder') return 1;
        
        // Files with children come before regular files
        const aHasChildren = a.children && (Array.isArray(a.children) ? a.children.length > 0 : Object.keys(a.children).length > 0);
        const bHasChildren = b.children && (Array.isArray(b.children) ? b.children.length > 0 : Object.keys(b.children).length > 0);
        
        if (aHasChildren && !bHasChildren && a.type === 'file' && b.type === 'file') return -1;
        if (!aHasChildren && bHasChildren && a.type === 'file' && b.type === 'file') return 1;
        
        // Alphabetical within same category
        return a.name.localeCompare(b.name);
      });
  }

  return objectToArray(tree);
}

